import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, useId, type ReactNode, type TextareaHTMLAttributes } from 'react';

import { cn } from '../lib/cn';

const textareaStyles = cva(
  'block w-full rounded-md border bg-bg-canvas text-fg-primary placeholder:text-fg-muted px-3 py-2 transition-base ease-token focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:opacity-50 disabled:bg-bg-subtle disabled:cursor-not-allowed resize-y',
  {
    variants: {
      tone: {
        default: 'border-border-subtle hover:border-border-strong',
        error: 'border-danger focus-visible:ring-danger',
      },
    },
    defaultVariants: { tone: 'default' },
  },
);

export interface TextareaProps
  extends TextareaHTMLAttributes<HTMLTextAreaElement>, VariantProps<typeof textareaStyles> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, tone, label, hint, error, id, rows = 4, ...props },
  ref,
) {
  const reactId = useId();
  const textareaId = id ?? reactId;
  const describedById = hint || error ? `${textareaId}-desc` : undefined;
  const resolvedTone = error ? 'error' : tone;

  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label htmlFor={textareaId} className="text-fg-primary text-sm font-medium">
          {label}
        </label>
      )}
      <textarea
        ref={ref}
        id={textareaId}
        rows={rows}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedById}
        className={cn(textareaStyles({ tone: resolvedTone }), className)}
        {...props}
      />
      {(hint || error) && (
        <p id={describedById} className={cn('text-xs', error ? 'text-danger' : 'text-fg-muted')}>
          {error ?? hint}
        </p>
      )}
    </div>
  );
});
